import * as THREE from 'three';
import { trace } from '../core/trace.js';
import { installStyles, removeStyles } from './style.js';
import { el, clamp, clamp01, damp, setStyle, ease } from './util.js';
import { Crosshair } from './crosshair.js';
import { Hitmarkers } from './hitmarkers.js';
import { DamageArcs } from './damage.js';
import { HealthFx } from './health.js';
import { AmmoPanel } from './ammo.js';
import { Killfeed } from './killfeed.js';
import { ProgressionBadge } from './progression.js';
import { Compass, MatchBar } from './compass.js';
import { Minimap } from './minimap.js';
import { WorldMarkers } from './markers.js';
import { Prompt, Banner } from './prompts.js';
import { PauseMenu } from './menu.js';
import { MainMenu } from './mainmenu.js';
import { DebugReport } from './debugreport.js';
import { CombatDemo } from './demo.js';
import { TouchControls } from './touchcontrols.js';
import { DeathScreen } from './death.js';

const MAX_BLIPS = 48;

/**
 * ===========================================================================
 * HUD / UI subsystem
 * ===========================================================================
 *
 * A DOM+CSS overlay (see style.js for the design system) driven entirely from
 * `lateUpdate`, after the camera has reached its final transform for the frame.
 * Nothing animates on a CSS keyframe or transition: every value is integrated
 * from `dt` here, which is what makes the capture harness deterministic and
 * lets the whole HUD freeze correctly when the game is paused.
 *
 * ---------------------------------------------------------------------------
 * PUBLIC API — `const ui = ctx.get('ui')`
 * ---------------------------------------------------------------------------
 *   ui.hitmarker(kind)                  'hit' | 'armour' | 'head' | 'kill'
 *   ui.damageNumber(worldPos, n, kind)  'hit' | 'hs' | 'armour' | 'kill'
 *   ui.hurt(amount, dirX, dirZ)         directional arc + flash + flinch
 *   ui.killfeed.push({attacker,victim,headshot,mine,attackerFriendly})
 *   ui.banner.show(title, sub, life)    kill / objective confirmation
 *   ui.setPrompt({key,text,sub,progress}) / ui.clearPrompt()
 *   ui.setObjectives([{position,label,name}])
 *   ui.setBlips([{x,z,kind:'enemy'|'friend',heading}])
 *   ui.spawnGrenade(worldPos, fuse)
 *   ui.setMatch({scoreUs,scoreThem,timeLeft,mode})
 *   ui.setHudVisible(bool)              hide everything (cinematics)
 *   ui.pause() / ui.resume() / ui.menu.toggle()
 *   ui.debugState('combat'|'menu'|'clean')
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS SUBSYSTEM READS FROM OTHERS (all optional, all duck-typed)
 * ---------------------------------------------------------------------------
 *   weapons.getHudState() -> { name, mode, ammo, reserve, magSize, reloading,
 *                              reloadProgress, ads, spread, lethalCount,
 *                              tacticalCount }
 *   player.getHudState()  -> { health, maxHealth, armour, maxArmour, regen,
 *                              move, sprint, crouch, ads, airborne, position }
 *                            (or plain `player.health` / `player.position`)
 *   ai.getHudActors()     -> [{ position, alive, friendly, heading }]
 *   audio.playUi(id, gain) | audio.play(id) — hit ticks, heartbeat, warnings
 *
 * Events consumed: weapon:fire, weapon:reload, damage:dealt, damage:taken,
 * actor:death, player:state, explosion, resize.
 * Events emitted:  ui:pause, ui:quality, ui:sensitivity, ui:fov, ui:setting.
 */
export class UiSystem {
  static id = 'ui';
  static deps = ['render'];

  async init(ctx) {
    this.ctx = ctx;
    this.rng = ctx.rng.fork();
    installStyles();

    const host = document.getElementById('ui') ?? document.body;
    this.root = el('div', 'ow-hud', host);

    // Stacking order: hurt overlays sit under the HUD, the menu over everything.
    this.hurtLayer = el('div', 'ow-layer', this.root);
    this.worldLayer = el('div', 'ow-layer', this.root);
    this.centreLayer = el('div', 'ow-layer', this.root);
    this.chromeLayer = el('div', 'ow-layer', this.root);

    this.health = new HealthFx(this.hurtLayer, this.chromeLayer);
    this.flashLayer = el('div', 'ow-flashbang', this.hurtLayer);
    this._flashT = 1;
    this._flashPeak = 0;
    this.markers = new WorldMarkers(this.worldLayer, this.rng.fork());
    this.arcs = new DamageArcs(this.centreLayer);
    this.crosshair = new Crosshair(this.centreLayer);
    this.scope = el('div', 'ow-scope', this.centreLayer);
    el('i', 'ow-scope-ring', this.scope);
    el('i', 'ow-scope-h', this.scope);
    el('i', 'ow-scope-v', this.scope);
    el('i', 'ow-scope-dot', this.scope);
    this.hit = new Hitmarkers(this.centreLayer);
    this.minimap = new Minimap(this.chromeLayer, this.rng.fork());
    this.compass = new Compass(this.chromeLayer);
    this.matchBar = new MatchBar(this.chromeLayer);
    this.killfeed = new Killfeed(this.chromeLayer);
    this.progression = new ProgressionBadge(this.chromeLayer);
    this.ammo = new AmmoPanel(this.chromeLayer);
    this.prompt = new Prompt(this.chromeLayer);
    this.banner = new Banner(this.chromeLayer);
    this.menu = new PauseMenu(this.root, ctx);
    this.touchControls = new TouchControls(this.root, ctx.input);
    this.deathScreen = new DeathScreen(this.root, {
      onReturnHome: () => this.returnHome(),
      onRespawn: () => this._respawnLocalPlayer(),
    });
    this.debugReport = new DebugReport(host, ctx);
    // main.js mounts the homescreen before expensive 3D initialization, so a
    // first visit never sits on a blank canvas. Adopt that screen here once
    // match systems are ready; the fallback keeps UiSystem standalone-safe.
    this.mainMenu = ctx.homeMenu ?? new MainMenu(host, ctx);
    this.mainMenu.setOnPlay((selection) => this.startMatch(selection));
    this.mainMenu.setOnQueue((selection, handlers) => this.queueOnline(selection, handlers));
    this.mainMenu.onQueueCancel = () => this.cancelOnlineQueue();

    this.health.onBeat = (i) => this.sfx('heartbeat', 0.35 + i * 0.5);

    /** Single source of truth for everything the HUD draws. */
    this.state = {
      health: 1000,
      maxHealth: 1000,
      armour: 0,
      maxArmour: 150,
      regen: false,
      ammo: 30,
      reserve: 210,
      magSize: 30,
      reloading: false,
      reloadProgress: 0,
      weaponName: 'M4A1',
      fireMode: 'AUTO',
      lethalCount: 2,
      tacticalCount: 1,
      move: 0,
      sprint: false,
      crouch: false,
      ads: false,
      airborne: false,
      baseSpread: 5.5,
      scoreUs: 0,
      scoreThem: 0,
      timeLeft: 600,
      mode: 'TDM',
      offlineMode: false,
      enemiesRemaining: 0,
      /** true when no player/weapons subsystem is driving us (stub-safe demo) */
      simulate: false,
      time: 0,
    };

    this.k = 1;
    this.vw = 1920;
    this.vh = 1080;
    this.hudVisible = 1;
    this.hudTarget = 1;
    this._lastRaw = ctx.time.raw;
    this._lastKillAt = -10;
    this._regenTimer = 0;
    this._hadPointerLock = false;
    this.matchActive = false;
    this.matchSession = 'offline';
    this.matchMode = 'TDM'; // track current match mode for restart
    this.respawnsRemaining = 5;
    this._spawnCursor = 0;
    this._returnTimer = 0;
    this._deathActive = false;
    this._bakeFrame = 0;

    this._pos = new THREE.Vector3();
    this._prevPos = new THREE.Vector3();
    this._dir = new THREE.Vector3();
    this._tmp = new THREE.Vector3();
    this._objectives = [];
    this._compassObjs = [];
    this._blips = new Array(MAX_BLIPS);
    for (let i = 0; i < MAX_BLIPS; i++) this._blips[i] = { x: 0, z: 0, kind: 'enemy', heading: 0 };
    this._blipCount = 0;
    this._blipView = [];

    this.demo = null;

    this._unsubs = [];
    const on = (type, fn) => this._unsubs.push(ctx.events.on(type, fn));

    on('weapon:fire', (e) => {
      // `src/net` replays other players' shots through this event so fx, audio
      // and ai all react to them. Our crosshair must not: it only blooms for
      // rounds this player fired.
      if (e?.remote) return;
      this.crosshair.onFire(e?.recoil ?? 1);
      if (this.state.simulate) return;
      const w = this._weaponState();
      if (!w) this.state.ammo = Math.max(0, this.state.ammo - 1);
    });

    on('weapon:reload', (e) => {
      const s = this.state;
      if (e?.phase === 'start') {
        s.reloading = true;
        s.reloadProgress = 0;
      } else if (e?.phase === 'end') {
        s.reloading = false;
        if (!this._weaponState()) {
          const take = Math.min(s.magSize - s.ammo, s.reserve);
          s.ammo += take;
          s.reserve -= take;
        }
      }
    });

    on('damage:dealt', (e) => {
      if (!e) return;
      // The payload means "damage dealt TO e.target". `ai` uses it for enemy
      // rounds that connect with the player, which must not draw a hitmarker or
      // a "YOU killed" killfeed row — that arrives as `damage:taken` below.
      if (this._isPlayerTarget(e.target)) return;
      const kind = e.killed ? 'kill' : e.headshot ? 'head' : e.armour ? 'armour' : 'hit';
      this.hitmarker(kind);
      if (e.point) {
        this.damageNumber(
          e.point,
          e.amount ?? 0,
          e.killed ? 'kill' : e.headshot ? 'hs' : e.armour ? 'armour' : 'hit'
        );
      }
      if (e.killed) {
        this._lastKillAt = ctx.time.elapsed;
        this.killfeed.push({
          attacker: 'YOU',
          victim: e.target?.name ?? e.name ?? 'ENEMY',
          headshot: !!e.headshot,
          mine: true,
        });
        const player = ctx.peek('player');
        const gained = player?.awardFightXp?.({ headshot: !!e.headshot, online: false })
          ?? (e.headshot ? 150 : 100);
        const coins = player?.progression?.authenticated ? ' · +10 COINS' : '';
        this.banner.show('Enemy Eliminated', `+${gained} XP${coins}${e.headshot ? ' · HEADSHOT' : ''}`);
        this.state.scoreUs++;
      }
    });

    on('damage:taken', (e) => {
      const amount = e?.amount ?? 10;
      if (e?.health !== undefined) this.state.health = e.health;
      else this.state.health = Math.max(0, this.state.health - amount);
      let dx = 0;
      let dz = 1;
      if (e?.from) {
        this._tmp.copy(e.from).sub(this._playerPos());
        dx = this._tmp.x;
        dz = this._tmp.z;
      }
      this.hurt(amount, dx, dz);
    });

    on('actor:death', (e) => {
      // Multiplayer deaths get their killfeed row from `src/net`, which knows
      // both real callsigns; this generic row would duplicate it as
      // "ENEMY killed OPERATOR".
      if (e?.actor?.isRemotePlayer) return;
      if (ctx.time.elapsed - this._lastKillAt < 0.3) return; // already credited
      this.killfeed.push({
        attacker: e?.by?.name ?? 'ENEMY',
        victim: e?.actor?.name ?? 'OPERATOR',
        attackerFriendly: false,
      });
    });

    on('explosion', (e) => {
      if (!e?.position) return;
      this._tmp.copy(e.position).sub(this._playerPos());
      const d = this._tmp.length();
      if (d < (e.radius ?? 6) * 2.5) this.crosshair.onFlinch(0.6);
    });

    on('flashbang', (e) => this._onFlashbang(e));

    on('player:state', (e) => {
      if (!e) return;
      const s = this.state;
      if (e.ads !== undefined) s.ads = !!e.ads;
      if (e.sprinting !== undefined) s.sprint = !!e.sprinting;
      if (e.stance !== undefined) s.crouch = e.stance === 'crouch' || e.stance === 'prone';
    });

    on('player:death', (e) => this._onPlayerDeath(e));
    on('player:respawn', () => this._onPlayerRespawn());
    on('actor:respawn', (e) => {
      if (!this.matchActive || this.matchSession !== 'offline' || e?.actor?.remote) return;
      const left = Math.max(0, e?.respawnsRemaining ?? 0);
      this.banner.show('Enemy Redeployed', `${left} RESPAWN${left === 1 ? '' : 'S'} REMAINING`, 2.2);
    });
    on('match:respawns-exhausted', (e) => {
      if (this.matchActive && this.matchSession === 'offline' && e?.side === 'opponents') {
        this._finishMatchAndRestart('VICTORY');
      }
    });
    on('match:round-over', (e) => this._finishMatch(e?.result, e));

    this.resize(ctx.canvas.clientWidth || innerWidth, ctx.canvas.clientHeight || innerHeight, ctx);
    this._prevPos.copy(this._playerPos());
  }

  /* ------------------------------------------------------------- helpers -- */

  _weaponState() {
    const w = this.ctx.peek('weapons');
    if (!w) return null;
    const s = typeof w.getHudState === 'function' ? w.getHudState() : w.hudState ?? null;
    return s && typeof s === 'object' ? s : null;
  }

  /** True when a `damage:dealt` payload is aimed at the local player. */
  _isPlayerTarget(t) {
    if (!t) return false;
    return t === 'player' || t === this.ctx.peek('player') || t.isPlayer === true;
  }

  _playerState() {
    const p = this.ctx.peek('player');
    if (!p) return null;
    const s = typeof p.getHudState === 'function' ? p.getHudState() : p.hudState ?? null;
    return s && typeof s === 'object' ? s : null;
  }

  _playerPos() {
    const p = this.ctx.peek('player');
    const pos = p?.position ?? p?.getPosition?.();
    if (pos && pos.isVector3) return this._pos.copy(pos);
    return this._pos.copy(this.ctx.camera.position);
  }

  /** Fire-and-forget audio; the audio subsystem may not exist yet. */
  sfx(id, gain = 1) {
    const a = this.ctx.peek('audio');
    if (!a) return;
    try {
      if (typeof a.playUi === 'function') a.playUi(id, gain);
      else if (typeof a.play === 'function') a.play(id, { gain });
      else if (typeof a.sfx === 'function') a.sfx(id, gain);
    } catch {
      /* audio is optional feedback — never let it break the HUD */
    }
  }

  /* ---------------------------------------------------------------- api --- */

  hitmarker(kind = 'hit') {
    this.hit.spawn(kind);
    this.crosshair.onHit();
    this.sfx(
      kind === 'kill' ? 'hit_kill' : kind === 'head' ? 'hit_head' : kind === 'armour' ? 'hit_armour' : 'hit_flesh',
      kind === 'kill' ? 1 : 0.7
    );
  }

  damageNumber(worldPos, amount, kind = 'hit') {
    this.markers.spawnDamage(worldPos, amount, kind);
  }

  /** Incoming damage: arc toward the source, screen flash, reticle flinch. */
  hurt(amount = 10, dirX = 0, dirZ = 1) {
    const i = clamp01(amount / 40);
    this.arcs.spawn(dirX, dirZ, 0.45 + i * 0.55);
    this.health.onDamage(i);
    this.crosshair.onFlinch(0.5 + i);
    this._regenTimer = 0;
    this.state.regen = false;
    this.sfx('player_hurt', 0.6 + i * 0.4);
  }

  setPrompt(p) {
    this.prompt.set(p);
  }

  clearPrompt() {
    this.prompt.clear();
  }

  setObjectives(list) {
    this._objectives = list ?? [];
  }

  addObjective(o) {
    this._objectives.push(o);
  }

  removeObjective(id) {
    const i = this._objectives.findIndex((o) => o.id === id);
    if (i >= 0) this._objectives.splice(i, 1);
  }

  /** Copies into a preallocated array — the caller's array is not retained. */
  setBlips(list) {
    const n = Math.min(list?.length ?? 0, MAX_BLIPS);
    for (let i = 0; i < n; i++) {
      const src = list[i];
      const dst = this._blips[i];
      dst.x = src.x ?? src.position?.x ?? 0;
      dst.z = src.z ?? src.position?.z ?? 0;
      dst.kind = src.kind ?? (src.friendly ? 'friend' : 'enemy');
      dst.heading = src.heading ?? 0;
    }
    this._blipCount = n;
  }

  spawnGrenade(worldPos, fuse = 2.4) {
    this.markers.spawnGrenade(worldPos, fuse);
    this.sfx('grenade_warn', 0.6);
  }

  /** A flashbang went off somewhere — whiteout the screen if it's close and
   * unoccluded, same falloff/line-of-sight shape as `_onExplosion` elsewhere. */
  _onFlashbang(e) {
    if (!e?.position) return;
    const phys = this.physics ?? (this.physics = this.ctx.peek('physics'));
    const eye = this.ctx.camera.position;
    const r = e.radius ?? 9;
    const d = this._tmp.copy(e.position).distanceTo(eye);
    if (d > r) return;
    const clear = phys?.lineOfSight ? phys.lineOfSight(e.position, eye, phys.MASK?.EXPLOSION) : true;
    if (!clear) return;
    const falloff = clamp01(1 - d / r);
    this._flashPeak = Math.max(this._flashPeak * (1 - this._flashT), falloff);
    this._flashT = 0;
  }

  /** Bright instantly, then a long fade — a flashbang blinds for seconds, not
   * the 180ms of a hit flash. */
  _tickFlashbang(dt) {
    if (this._flashT >= 1) return;
    const fadeTime = 1.2 + this._flashPeak * 2.6;
    this._flashT = Math.min(1, this._flashT + dt / fadeTime);
    const a = this._flashPeak * (1 - ease.outCubic(this._flashT));
    setStyle(this.flashLayer, 'opacity', a.toFixed(3));
  }

  setMatch(m) {
    Object.assign(this.state, m);
  }

  setHudVisible(v) {
    this.hudTarget = v ? 1 : 0;
  }

  pause() {
    this.menu.show();
  }

  resume() {
    this.menu.close();
  }

  _onPlayerDeath(e) {
    if (this._deathActive) return;
    trace('player died -> death screen', String(e?.killer ?? '(no killer)'));
    // Stop every gameplay input path: keyboard/mouse, gamepad-backed gameplay,
    // and the mobile controls. The death screen remains clickable on its own.
    this.ctx.peek('player')?.setControlEnabled?.(false);
    this.ctx.input.clearTouchControls?.();
    this.ctx.input.enabled = false;
    this.touchControls?.setActive(false);
    this._deathActive = true;
    if (!this.matchActive) {
      document.exitPointerLock?.();
      this.deathScreen.showFinal({ killer: e?.killer, scoreUs: this.state.scoreUs, scoreThem: this.state.scoreThem });
      return;
    }

    if (this.matchSession === 'online') {
      const net = this.ctx.peek('net');
      const lives = Math.max(0, e?.respawnsLeft ?? net?.respawnsLeft ?? 0);
      this.deathScreen.showRespawn(e?.killer, {
        livesRemaining: lives,
        delay: 5,
        autoRespawn: true,
        onRespawn: () => net?.socket?.send?.({ t: 'respawn' }),
      });
      return;
    }

    this.state.scoreThem++;
    if (this.respawnsRemaining > 0) {
      this.respawnsRemaining--;
      this.deathScreen.showRespawn(e?.killer, {
        livesRemaining: this.respawnsRemaining,
        delay: 3,
        autoRespawn: true,
        onRespawn: () => this._respawnLocalPlayer(),
      });
    } else {
      document.exitPointerLock?.();
      this._finishMatch('DEFEAT', { killer: e?.killer });
    }
  }

  _respawnLocalPlayer() {
    if (!this.matchActive || this.matchSession !== 'offline') return;
    const world = this.ctx.peek('world');
    const count = world?.spawnPoints?.length ?? 1;
    this._spawnCursor = (this._spawnCursor + 1) % Math.max(1, count);
    this.ctx.peek('player')?.respawn?.(this._spawnCursor);
    this.ctx.peek('weapons')?.resetForRespawn?.();
    this._onPlayerRespawn();
  }

  _onPlayerRespawn() {
    this._deathActive = false;
    this.deathScreen.hide();
    if (!this.matchActive) return;
    this.ctx.input.enabled = true;
    this.ctx.peek('player')?.setControlEnabled?.(true);
    this.ctx.input?.requestPointerLock?.();
  }

  _finishMatch(result, details = {}) {
    if (!this.matchActive) return;
    this.matchActive = false;
    this._deathActive = true;
    this.ctx.peek('player')?.setControlEnabled?.(false);
    this.ctx.input.enabled = false;
    document.exitPointerLock?.();
    this.deathScreen.showFinal({
      result: result ?? (this.state.scoreUs > this.state.scoreThem ? 'VICTORY' : this.state.scoreUs < this.state.scoreThem ? 'DEFEAT' : 'DRAW'),
      scoreUs: details.scoreUs ?? this.state.scoreUs,
      scoreThem: details.scoreThem ?? this.state.scoreThem,
      detail: details.coins ? `BATTLE COMPLETE · +${details.coins} COINS` : 'RETURNING TO HOME BASE',
      killer: details.killer,
    });
    clearTimeout(this._returnTimer);
    this._returnTimer = setTimeout(() => this.returnHome(), 5000);
  }

  /** Finish an offline match with victory and schedule a restart. */
  _finishMatchAndRestart(result, details = {}) {
    if (!this.matchActive) return;
    this.matchActive = false;
    this._deathActive = true;
    this.ctx.peek('player')?.setControlEnabled?.(false);
    this.ctx.input.enabled = false;
    document.exitPointerLock?.();
    this.deathScreen.showFinal({
      result: result ?? 'VICTORY',
      scoreUs: details.scoreUs ?? this.state.scoreUs,
      scoreThem: details.scoreThem ?? this.state.scoreThem,
      detail: 'MATCH COMPLETE · REDEPLOYING',
      killer: details.killer,
    });
    clearTimeout(this._returnTimer);
    this._returnTimer = setTimeout(() => this.restartMatch(), 5000);
  }

  /** Restart the current offline match with a fresh bot roster and reset player state. */
  restartMatch() {
    trace('restartMatch');
    this.deathScreen.hide();
    clearTimeout(this._returnTimer);
    this._returnTimer = 0;
    this._deathActive = false;
    // Reset the match with the same session and mode
    this.startMatch({ session: this.matchSession, mode: this.matchMode });
  }

  /** Leave a completed/dead match without reviving the player in-place. */
  returnHome() {
    // The home screen reappearing mid-match has exactly one legitimate cause
    // (the death screen's only button) and is otherwise a bug, so say who asked.
    trace('returnHome', (new Error().stack || '').split('\n')[2]?.trim() ?? '?');
    this.deathScreen.hide();
    clearTimeout(this._returnTimer);
    this._returnTimer = 0;
    this.matchActive = false;
    this._deathActive = false;
    this.ctx.input.clearTouchControls?.();
    this.ctx.input.enabled = false;
    document.exitPointerLock?.();
    this.ctx.peek('player')?.setControlEnabled?.(false);
    this.cancelOnlineQueue();
    this.ctx.peek('net')?.setSessionMode?.('offline');
    this.ctx.peek('ai')?.setSessionMode?.('idle');
    this.mainMenu.show();
  }

  /** Called after every engine system has initialized, including networking. */
  showHome() {
    trace('showHome (boot)');
    this.mainMenu?.show();
  }

  /**
   * Boot hook, called once the frame loop is running: run a deploy the player
   * asked for while the engine was still coming up. Deliberately not done from
   * init() — see MainMenu.setOnPlay for why a half-built engine cannot service
   * one.
   */
  flushQueuedDeploy() {
    this.mainMenu?.flushQueuedDeploy?.();
  }

  /** Whether the player pressed GO before the engine could service it. */
  hasQueuedDeploy() {
    return Boolean(this.mainMenu?.hasQueuedDeploy?.());
  }

  /**
   * Called by the home screen once the player has selected online/offline and
   * a playlist. The systems themselves decide what "online" means: the network
   * client connects only for real-player matches and AI wakes only for offline
   * drills. This makes the choice functional rather than decorative.
   */
  startMatch(selection = {}) {
    const session = selection.session === 'online' ? 'online' : 'offline';
    const mode = ['1v1', '2v2', 'TDM', 'FFA'].includes(selection.mode) ? selection.mode : 'TDM';
    const onlineNames = {
      '1v1': '1V1 DUEL',
      '2v2': '2V2 FIRETEAM',
      TDM: 'TEAM DEATHMATCH',
      FFA: 'FREE FOR ALL',
    };
    const offlineNames = {
      '1v1': 'ONE-BOT DUEL',
      '2v2': 'FOUR-OPERATOR DRILL',
      TDM: 'TEAM DEATH DRILL',
      FFA: 'FREE-FOR-ALL DRILL',
    };
    const match = (session === 'online' ? onlineNames : offlineNames)[mode] ?? 'COMBAT DRILL';
    clearTimeout(this._returnTimer);
    this._returnTimer = 0;
    this.matchActive = true;
    this.matchSession = session;
    this.matchMode = mode;
    this.respawnsRemaining = 5;
    this._spawnCursor = 0;
    this._deathActive = false;

    try { this.ctx.peek('ai')?.setSessionMode?.(session, mode); } catch (err) { console.warn('[ui] AI session setup failed', err); }
    try {
      const net = this.ctx.peek('net');
      if (session === 'offline') {
        net?.setSessionMode?.('offline');
      } else {
        // ONLINE arrives here only from a verified `lobby` queue/challenge —
        // `queueOnline` below stashed which specific match that resolved to.
        const matchId = this._pendingMatchId ?? null;
        this._pendingMatchId = null;
        net?.connectToMatch?.(matchId, mode);
      }
    } catch (err) { console.warn('[ui] network session setup failed', err); }

    trace('startMatch', { session, mode, map: selection.map });
    this.setMatch({
      mode: match,
      scoreUs: 0,
      scoreThem: 0,
      timeLeft: mode === '1v1' || mode === '2v2' ? 300 : 600,
    });
    this.deathScreen.hide();
    this.mainMenu.hide();
    // MainMenu disables gameplay input to keep its cursor visible. Re-enable
    // it only after a real match selection, immediately before pointer lock.
    if (this.ctx.input) this.ctx.input.enabled = true;
    const player = this.ctx.peek('player');
    // Every deployment starts a clean life and replenished four-slot loadout.
    player?.respawn?.(0);
    this.ctx.peek('weapons')?.resetForRespawn?.();
    player?.setControlEnabled?.(true);
    this.ctx.input?.requestPointerLock?.();
    this.ctx.events.emit('match:start', { session, mode });
  }

  /**
   * Keep the home screen present while `lobby` (`src/net/lobby.js`) finds a
   * real match for this exact (mode, map) — matchmaking a real match on THAT
   * map is the point; the old version of this only ever counted bodies
   * already in the server's one shared arena, regardless of what they were
   * playing. Never falls back to local AI.
   */
  queueOnline(selection, handlers = {}) {
    this.cancelOnlineQueue();
    const lobby = this.ctx.peek('lobby');
    if (!lobby?.enabled) {
      handlers.onStatus?.({ status: 'UNAVAILABLE', players: 0, required: 0, detail: 'MULTIPLAYER SERVICE IS DISABLED' });
      return () => {};
    }

    let done = false;
    this._queueOff = this.ctx.events.on('lobby:queue', (state) => {
      if (!state || done) return;
      const short = Math.max(0, (state.required ?? 0) - (state.players ?? 0));
      handlers.onStatus?.({
        status: 'QUEUED',
        players: state.players ?? 0,
        required: state.required ?? 0,
        ready: false,
        detail: short > 0 ? `WAITING FOR ${short} MORE REAL PLAYER${short === 1 ? '' : 'S'}` : 'MATCH FOUND — DEPLOYING',
      });
    });
    this._matchReadyOff = this.ctx.events.on('lobby:match-ready', (info) => {
      if (done) return;
      done = true;
      this._queueOff?.();
      this._queueOff = null;
      this._matchReadyOff?.();
      this._matchReadyOff = null;
      this._lobbyErrOff?.();
      this._lobbyErrOff = null;
      // `startMatch` reads this to know which specific match to connect `net`
      // to instead of the server's persistent one — see `startMatch` above.
      this._pendingMatchId = info.matchId;
      handlers.onReady?.();
    });
    this._lobbyErrOff = this.ctx.events.on('lobby:error', (e) => {
      handlers.onStatus?.({ status: 'REJECTED', players: 0, required: 0, detail: String(e?.reason ?? 'unknown').toUpperCase() });
    });

    handlers.onStatus?.({ status: 'CONNECTING', players: 0, required: 0, detail: 'CONTACTING MULTIPLAYER SERVICE' });
    lobby.connect();
    // `LobbySystem.queue` buffers this on the socket's own pending-message
    // queue if the connection is still opening, and flushes it the moment it
    // is — no need to wait for a "connected" event before sending it.
    lobby.queue(selection.mode, selection.map);
    return () => this.cancelOnlineQueue();
  }

  cancelOnlineQueue() {
    this._queueOff?.();
    this._queueOff = null;
    this._matchReadyOff?.();
    this._matchReadyOff = null;
    this._lobbyErrOff?.();
    this._lobbyErrOff = null;
    // Only the QUEUE is cancelled — the lobby connection itself (presence,
    // being challengeable) is owned by `mainmenu.js`'s own enter/leave calls,
    // since the player may still be browsing the online players list.
    try { this.ctx?.peek?.('lobby')?.cancelQueue(); } catch { /* best effort */ }
  }

  /* --------------------------------------------------------------- debug -- */

  /**
   * Populate a representative state for screenshots / critics.
   * 'combat' runs the scripted firefight timeline in demo.js.
   */
  debugState(name = 'combat') {
    if (name === 'clean') {
      this.demo?.stop(this);
      this.demo = null;
      this.state.simulate = false;
      this.killfeed.clear();
      this.arcs.clear();
      this.hit.clear();
      this.markers.clear();
      this.clearPrompt();
      return { state: 'clean' };
    }
    if (name === 'menu') {
      this.debugState('combat');
      this.menu.show();
      return { state: 'menu' };
    }
    if (!this.demo) this.demo = new CombatDemo();
    this.demo.start(this);
    return { state: 'combat', frames: 'timeline keyed to frame 90' };
  }

  /* -------------------------------------------------------------- frame --- */

  lateUpdate(dt, ctx) {
    const t = ctx.time;
    const rawDt = clamp(t.raw - this._lastRaw, 0, 0.1);
    this._lastRaw = t.raw;
    const s = this.state;
    s.time = t.elapsed;

    // ---- pause -----------------------------------------------------------
    if (ctx.input.enabled && !ctx.input.frozen) {
      const openReport = ctx.input.pressed('Backquote') && this.matchActive && !this.menu.open && !this.debugReport.visible;
      if (openReport) {
        this._hadPointerLock = false;
        this.debugReport.open();
      } else {
        if (ctx.input.actionPressed('pause')) this.menu.toggle();
        // Losing pointer lock mid-match is the same intent as pressing Escape.
        if (ctx.input.pointerLocked) this._hadPointerLock = true;
        else if (this._hadPointerLock && !this.menu.open) {
          this._hadPointerLock = false;
          this.menu.show();
        }
      }
    }
    this.menu.update(rawDt);
    this.touchControls?.setActive(
      ctx.input.enabled && !ctx.input.frozen && !this.menu.open && !this.mainMenu?.visible
    );

    // ---- external state --------------------------------------------------
    // `simulate` means a scripted debug timeline owns the HUD numbers; letting
    // the live weapon/player state through would fight it every frame.
    const ws = s.simulate ? null : this._weaponState();
    if (ws) {
      if (ws.name) s.weaponName = ws.name;
      if (ws.mode) s.fireMode = ws.mode;
      if (ws.ammo !== undefined) s.ammo = ws.ammo;
      if (ws.reserve !== undefined) s.reserve = ws.reserve;
      if (ws.magSize !== undefined) s.magSize = ws.magSize;
      if (ws.reloading !== undefined) s.reloading = !!ws.reloading;
      if (ws.reloadProgress !== undefined) s.reloadProgress = ws.reloadProgress;
      if (ws.ads !== undefined) s.ads = !!ws.ads;
      if (ws.spread !== undefined) s.baseSpread = 4 + ws.spread * 40;
      if (ws.lethalCount !== undefined) s.lethalCount = ws.lethalCount;
      if (ws.tacticalCount !== undefined) s.tacticalCount = ws.tacticalCount;
    }
    const weaponSystem = ctx.peek('weapons');
    const scopeAmount = weaponSystem?.current?.class === 'sniper'
      ? clamp01(((weaponSystem.adsProgress ?? 0) - 0.2) / 0.65)
      : 0;
    setStyle(this.scope, 'opacity', scopeAmount.toFixed(3));
    setStyle(this.scope, 'display', scopeAmount < 0.004 ? 'none' : '');

    const ps = s.simulate ? null : this._playerState();
    const player = ctx.peek('player');
    if (ps) {
      if (ps.health !== undefined) s.health = ps.health;
      if (ps.maxHealth !== undefined) s.maxHealth = ps.maxHealth;
      if (ps.armour !== undefined) s.armour = ps.armour;
      else if (ps.armor !== undefined) s.armour = ps.armor;
      if (ps.regen !== undefined) s.regen = !!ps.regen;
      if (ps.move !== undefined) s.move = ps.move;
      if (ps.sprint !== undefined) s.sprint = !!ps.sprint;
      if (ps.crouch !== undefined) s.crouch = !!ps.crouch;
      if (ps.ads !== undefined) s.ads = !!ps.ads;
      if (ps.airborne !== undefined) s.airborne = !!ps.airborne;
    } else if (player && typeof player.health === 'number') {
      s.health = player.health;
    }

    // ---- offline match state -----------------------------------------------
    s.offlineMode = this.matchSession === 'offline' && this.matchActive;
    if (s.offlineMode) {
      const ai = ctx.peek('ai');
      const stats = ai?.matchStats;
      if (stats) {
        s.enemiesRemaining = (stats.alive ?? 0) + (stats.pending ?? 0) + (stats.respawnsRemaining ?? 0);
      }
      // Decrement offline match timer
      if (s.timeLeft > 0) {
        s.timeLeft = Math.max(0, s.timeLeft - ctx.time.dt);
        // When time expires in offline mode, end the match
        if (s.timeLeft <= 0 && this.matchActive) {
          const result = s.scoreUs > s.scoreThem ? 'VICTORY' : s.scoreUs < s.scoreThem ? 'DEFEAT' : 'DRAW';
          this._finishMatchAndRestart(result);
        }
      }
    }

    // ---- movement-derived reticle bloom (works with any player system) ----
    const pos = this._playerPos();
    if (!ps && !s.simulate) {
      this._dir.copy(pos).sub(this._prevPos);
      this._dir.y = 0;
      const speed = dt > 0 ? this._dir.length() / dt : 0;
      s.move = damp(s.move, clamp01(speed / 6.2), 12, Math.max(rawDt, 1e-3));
      if (!this._weaponState()) s.ads = ctx.input.ads && ctx.input.enabled;
    }
    this._prevPos.copy(pos);

    // ---- health regeneration when nobody else owns health ----------------
    if (!ps && !s.simulate && s.health < s.maxHealth) {
      this._regenTimer += dt;
      if (this._regenTimer > 4.5) {
        if (!s.regen) {
          s.regen = true;
          this.health.onRegenStart();
          this.sfx('regen', 0.4);
        }
        s.health = Math.min(s.maxHealth, s.health + dt * 24);
      }
    }

    // ---- demo timeline ---------------------------------------------------
    if (this.demo?.active) this.demo.update(this, dt);

    // ---- ai blips --------------------------------------------------------
    this._collectBlips();

    // ---- camera basis ----------------------------------------------------
    const m = ctx.camera.matrixWorld.elements;
    let rx = m[0];
    let rz = m[2];
    let fx = -m[8];
    let fz = -m[10];
    const rl = Math.hypot(rx, rz) || 1;
    const fl = Math.hypot(fx, fz) || 1;
    rx /= rl;
    rz /= rl;
    fx /= fl;
    fz /= fl;
    const heading = (Math.atan2(fx, -fz) * 180) / Math.PI;

    // ---- widgets ---------------------------------------------------------
    const hudGoal = this.hudTarget * (this.menu.open ? 0.15 : 1);
    this.hudVisible = damp(this.hudVisible, hudGoal, 10, rawDt);
    setStyle(this.chromeLayer, 'opacity', this.hudVisible.toFixed(3));
    setStyle(this.worldLayer, 'opacity', this.hudVisible.toFixed(3));
    setStyle(this.centreLayer, 'opacity', this.hudVisible.toFixed(3));

    this.crosshair.update(dt, s);
    this.hit.update(dt);
    this.arcs.update(dt, rx, rz, fx, fz);
    this.health.update(dt, s);
    this._tickFlashbang(dt);
    this.ammo.update(dt, s);
    this.killfeed.update(dt);
    this.matchBar.update(s);
    this.progression.update(ctx.peek('player')?.progression?.snapshot?.());
    this.prompt.update(dt);
    this.banner.update(dt);

    this._buildCompassObjectives(pos);
    this.compass.update(heading, this._compassObjs);

    this.markers.updateObjectives(this._objectives, ctx.camera, this.vw, this.vh, this.k);
    this.markers.updateGrenades(dt, ctx.camera, this.vw, this.vh, this.k);
    this.markers.updateDamage(dt, ctx.camera, this.vw, this.vh, this.k);

    // ---- minimap ---------------------------------------------------------
    if (!this.minimap.bakeDone && ++this._bakeFrame > 6 && this._bakeFrame % 20 === 0) {
      this.minimap.tryBake(ctx);
    }
    this._blipView.length = this._blipCount;
    for (let i = 0; i < this._blipCount; i++) this._blipView[i] = this._blips[i];
    this._mmState = this._mmState ?? { x: 0, z: 0, heading: 0, fov: 80, blips: null, objectives: null };
    this._mmState.x = pos.x;
    this._mmState.z = pos.z;
    this._mmState.heading = heading;
    this._mmState.fov = ctx.camera.fov;
    this._mmState.blips = this._blipView;
    this._mmState.objectives = this._mmObjs ?? (this._mmObjs = []);
    this._mmObjs.length = 0;
    for (const o of this._objectives) {
      if (!o.position) continue;
      this._mmObjs.push(o._mm ?? (o._mm = { x: 0, z: 0, label: o.label }));
      const last = this._mmObjs[this._mmObjs.length - 1];
      last.x = o.position.x;
      last.z = o.position.z;
      last.label = o.label;
    }
    this.minimap.draw(this._mmState);
  }

  _collectBlips() {
    if (this.demo?.active) return; // demo drives its own contacts
    const ai = this.ctx.peek('ai');
    const list = typeof ai?.getHudActors === 'function' ? ai.getHudActors() : ai?.actors ?? null;
    if (!Array.isArray(list)) return;
    let n = 0;
    for (let i = 0; i < list.length && n < MAX_BLIPS; i++) {
      const a = list[i];
      const p = a?.position ?? a?.pos;
      if (!p || a.alive === false || a.dead === true) continue;
      const b = this._blips[n++];
      b.x = p.x;
      b.z = p.z;
      b.kind = a.friendly ? 'friend' : 'enemy';
      b.heading = a.heading ?? (a.yaw !== undefined ? (a.yaw * 180) / Math.PI : 0);
    }
    this._blipCount = n;
  }

  _buildCompassObjectives(pos) {
    const out = this._compassObjs;
    out.length = 0;
    for (const o of this._objectives) {
      if (!o.position) continue;
      const dx = o.position.x - pos.x;
      const dz = o.position.z - pos.z;
      const bearing = (Math.atan2(dx, -dz) * 180) / Math.PI;
      out.push(o._cmp ?? (o._cmp = { bearing: 0, label: o.label, color: o.color }));
      const last = out[out.length - 1];
      last.bearing = bearing;
      last.label = o.label;
      last.color = o.color;
    }
    return out;
  }

  resize(w, h, ctx) {
    this.vw = w;
    this.vh = h;
    this.k = clamp(h / 1080, 0.62, 2.4);
    this.root.style.setProperty('--k', this.k.toFixed(4));
    this.crosshair.setScale(this.k);
    this.compass.setScale(this.k);
    this.minimap.resize(this.k);
  }

  dispose() {
    clearTimeout(this._returnTimer);
    for (const off of this._unsubs) off();
    this._unsubs.length = 0;
    this.crosshair.dispose();
    this.hit.dispose();
    this.arcs.dispose();
    this.health.dispose();
    this.ammo.dispose();
    this.killfeed.dispose();
    this.progression.dispose();
    this.compass.dispose();
    this.matchBar.dispose();
    this.minimap.dispose();
    this.markers.dispose();
    this.prompt.dispose();
    this.banner.dispose();
    this.menu.dispose();
    this.touchControls.dispose();
    this.deathScreen.dispose();
    this.debugReport.dispose();
    this.cancelOnlineQueue();
    this.mainMenu.dispose();
    this.root.remove();
    removeStyles();
  }
}
